import {
  Injectable,
  WritableSignal,
  Signal,
  computed,
  signal,
  effect,
  inject,
} from '@angular/core';
import { ApiService } from '../services/api-services/api.service';
import { CalendarEvent } from '../interfaces/interfaces';
import { catchError, firstValueFrom, of, tap } from 'rxjs';
import * as moment from 'moment';
import { UserStore } from './user.store';
import { FloorplanStore } from './floorplan.store';
import { PlacesStore } from './places.store';
import { HapticsService } from '../services/haptics-service/haptics.service';
import { AvatarService } from '../services/avatarService/avatar.service';

interface BookingsSortedByDate {
  [key: string]: CalendarEvent[];
}

@Injectable({
  providedIn: 'root',
})
export class BookingsStore {
  private _allBookingsSignal: WritableSignal<CalendarEvent[]> = signal<
    CalendarEvent[]
  >([]);
  private _bookingsTypeFilterSignal: WritableSignal<
    'all' | 'without-resource' | 'with-resource'
  > = signal<'all' | 'without-resource' | 'with-resource'>('all');
  private _showExpiredFilterSignal: WritableSignal<boolean> =
    signal<boolean>(false);
  private _loadingSignal: WritableSignal<boolean> = signal<boolean>(false);
  private _errorSignal: WritableSignal<string | null> = signal<string | null>(
    null
  );

  //Computed signals
  public allBookingsRaw: Signal<CalendarEvent[]> = this._allBookingsSignal.asReadonly();
  public allBookings: Signal<CalendarEvent[]> = computed(() =>
    this.mapAndFilterBookingsByShowExpired(this._allBookingsSignal())
  );

  public myBookings: Signal<BookingsSortedByDate> = computed(() =>
    this.sortBookingsByDate(
      this.filterBookingsByTypeAndOrganizer(
        this.allBookings(),
        true,
        this.bookingsTypeFilter()
      )
    )
  );

  public myInvitations: Signal<BookingsSortedByDate> = computed(() =>
    this.sortBookingsByDate(
      this.filterBookingsByTypeAndOrganizer(
        this.allBookings(),
        false,
        this.bookingsTypeFilter()
      )
    )
  );
  public pendingInvitationsCount: Signal<number> = computed(() =>
    this.getPendingInvitationsCount(
      this.myInvitations(),
      this.userStore.user()?.mail
    )
  );

  public bookingsTypeFilter: Signal<
    'all' | 'without-resource' | 'with-resource'
  > = this._bookingsTypeFilterSignal.asReadonly();

  public showExpiredFilter: Signal<boolean> =
    this._showExpiredFilterSignal.asReadonly();
  public allBookingsFull: Signal<CalendarEvent[]> =
    this._allBookingsSignal.asReadonly();

  public loading: Signal<boolean> = this._loadingSignal.asReadonly();
  public error: Signal<string | null> = this._errorSignal.asReadonly();

  //Constructor
  private apiService: ApiService = inject(ApiService);
  private userStore: UserStore = inject(UserStore);
  private floorplanStore: FloorplanStore = inject(FloorplanStore);
  private placesStore: PlacesStore = inject(PlacesStore);
  private hapticsService: HapticsService = inject(HapticsService);
  private avatarService: AvatarService = inject(AvatarService);
  constructor() {}

  //   Actions
  public setBookingsTypeFilter(
    bookingsType: 'all' | 'without-resource' | 'with-resource'
  ) {
    this._bookingsTypeFilterSignal.set(bookingsType);
  }

  public setShowExpiredFilter(showExpired: boolean) {
    this._showExpiredFilterSignal.set(showExpired);
  }

  public resetStoreData() {
    this._allBookingsSignal.set([]);
    this._bookingsTypeFilterSignal.set('all');
    this._showExpiredFilterSignal.set(false);
    this._loadingSignal.set(false);
    this._errorSignal.set(null);
  }

  // API calls
  public getMyBookings() {
    this._allBookingsSignal.set([]);
    this._loadingSignal.set(true);
    this._errorSignal.set(null);
    return firstValueFrom(
      this.apiService.getBookingsFromMyCalendarManager().pipe(
        tap((bookings: CalendarEvent[]) => {
          this.mapBookingsToFloorplans(bookings);
          this.mapBookingsToAvatars(bookings);

          this._allBookingsSignal.set(bookings);
          this._loadingSignal.set(false);
        }),
        catchError((error) => {
          this._errorSignal.set(error.message || 'Failed to fetch bookings');
          this._loadingSignal.set(false);
          return of([]);
        })
      )
    );
  }

  public makeNewBooking(eventRequest: any) {
    return this.apiService
      .createCalendarEventNew(eventRequest, eventRequest.isOnPrem)
      .pipe(
        tap((res) => {
          // Handle success
          console.log('Event created successfully:', res);
          // Set place as pending
          this.placesStore.setPlacePending(eventRequest.placeEmailAddress);
          // Trigger haptic feedback
          this.hapticsService.lightImpact();
        }),
        catchError((ex) => {
          console.log('Error creating event:', ex);
          // Trigger haptic feedback even on error
          this.hapticsService.lightImpact();
          // Re-throw the error so the component can handle the specific error messages
          throw ex;
        })
      );
  }

  public acceptInvitation(id: string, isSeries: boolean = false) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService.InvitationRespond(id, 'Accept').pipe(
      catchError((ex) => {
        console.log('Error accepting invitation:', ex);
        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      })
    );
  }

  public declineInvitation(id: string, isSeries: boolean = false) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService.InvitationRespond(id, 'Decline').pipe(
      catchError((ex) => {
        console.log('Error declining invitation:', ex);
        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      })
    );
  }

  public tentativelyAcceptInvitation(id: string, isSeries: boolean = false) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService.InvitationRespond(id, 'TentativelyAccept').pipe(
      catchError((ex) => {
        console.log('Error tentatively accepting invitation:', ex);
        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      })
    );
  }

  public deleteBooking(id: string, isSeries: boolean = false) {
    this.setMeetingsLoading(id, isSeries);
    return this.apiService.deleteCalendarEvent(id).pipe(
      catchError((ex) => {
        console.log('Error creating event:', ex);
        this.setMeetingsLoading(id, isSeries, false);
        // Trigger haptic feedback even on error
        this.hapticsService.lightImpact();
        // Re-throw the error so the component can handle the specific error messages
        throw ex;
      })
    );
  }

  // Websocket events callbacks

  public deleteBookingLocal(event: CalendarEvent) {
    this._allBookingsSignal.update((bookings) =>
      bookings.filter((booking) =>
        event.seriesMasterId
          ? booking.seriesMasterId != event.seriesMasterId
          : booking.id != event.id
      )
    );
  }

  public addBookingLocal(event: CalendarEvent) {
    if (event.place != undefined) {
      event.place = this.placesStore.getPlaceByEmailAddress(
        event.place.emailAddress
      );
    }

    this._allBookingsSignal.update((bookings) => [...bookings, event]);
  }

  public updateBookingLocal(event: CalendarEvent) {
    if (event.place != undefined) {
      event.place = this.placesStore.getPlaceByEmailAddress(
        event.place.emailAddress
      );
    }

    this._allBookingsSignal.update((bookings) =>
      bookings.map((booking) =>
        booking.id === event.id ? { ...event } : booking
      )
    );
  }

  //   Helpers functions

  public mapAndFilterBookingsByShowExpired(bookings: CalendarEvent[]) {
    bookings.forEach((booking) => this.isMeetingExpired(booking));
    if (this.showExpiredFilter()) {
      return bookings;
    }
    return bookings.filter((booking) => booking.isExpired == false);
  }

  private isMeetingExpired(booking: CalendarEvent) {
    booking.isExpired = booking.endTime < new Date().toISOString();
    return booking.isExpired;
  }

  private mapBookingsToFloorplans(bookings: CalendarEvent[]) {
    try {
      const floorplans = this.floorplanStore.allFloorplansList();
      if (!floorplans || floorplans.length === 0) {
        console.warn('No floorplans available for mapping');
        return;
      }

      bookings.forEach((booking) => {
        try {
          if (!booking.place || !booking.place.emailAddress) {
            return; // Skip this booking if place or emailAddress is missing
          }

          const matchingFloorplan = floorplans.find((floorplan) => {
            try {
              // Skip data URLs
              if (floorplan.floorMapImage.startsWith('data')) {
                return false;
              }

              // Parse JSON safely
              const parsedImage = JSON.parse(floorplan.floorMapImage);

              // Check if objects array exists and find matching object
              if (parsedImage.objects && Array.isArray(parsedImage.objects)) {
                return parsedImage.objects.some(
                  (object) => object.name === booking.place.emailAddress
                );
              }

              return false;
            } catch (parseError) {
              console.warn('Failed to parse floorplan image JSON:', parseError);
              return false;
            }
          });

          booking.place.floorMapId = matchingFloorplan?._id;
        } catch (bookingError) {
          console.warn(
            'Error processing booking for floorplan mapping:',
            bookingError
          );
        }
      });
    } catch (error) {
      console.error('Error in mapBookingsToFloorplans:', error);
    }
  }

  private mapBookingsToAvatars(bookings: CalendarEvent[]) {
    const avatars = this.avatarService.avatarList();
    bookings.forEach((booking) => {
      const avatar = avatars.find(
        (avatar) => avatar.upn == booking?.place?.emailAddress
      );
      if (booking.place) {
        booking.place.avatar = avatar?.avatar;
      }
    });
  }

  private setMeetingsLoading(
    id: string,
    isSeries: boolean = false,
    loading: boolean = true
  ) {
    isSeries
      ? this._allBookingsSignal.update((bookings) =>
          bookings.map((booking) => {
            const userResponse = booking.attendees.find(
              (attendee) =>
                attendee?.userPrincipalName ==
                this.userStore.user()?.userPrincipalName
            )?.response;
            const didUserNotRespond =
              userResponse == 'None' ||
              userResponse == 'NotResponded' ||
              userResponse == null ||
              userResponse == '';
            return booking.seriesMasterId == id && didUserNotRespond
              ? { ...booking, isLoading: loading }
              : booking;
          })
        )
      : this._allBookingsSignal.update((bookings) =>
          bookings.map((booking) =>
            booking.id == id ? { ...booking, isLoading: loading } : booking
          )
        );
  }

  private getPendingInvitationsCount(
    bookings: BookingsSortedByDate,
    loggedInUserEmail: string
  ) {
    return Object.values(bookings)
      .flat()
      .filter((event) => {
        if (event.isOrganizer == false && event.isCancelled == false) {
          let meAsAnAttendee = event.attendees.find((attendee) => {
            if (attendee)
              return attendee?.userPrincipalName == loggedInUserEmail;
          });
          if (
            meAsAnAttendee &&
            (meAsAnAttendee.response == 'None' ||
              meAsAnAttendee.response == 'NotResponded')
          )
            return true;
        }
      }).length;
  }

  private filterBookingsByTypeAndOrganizer(
    bookings: CalendarEvent[],
    isOrganizer: boolean,
    type: 'all' | 'without-resource' | 'with-resource' = 'all'
  ) {
    if (type == 'all') {
      return bookings.filter(
        (booking: CalendarEvent) => booking.isOrganizer == isOrganizer
      );
    } else if (type == 'without-resource') {
      return bookings.filter(
        (booking: CalendarEvent) =>
          booking.isOrganizer == isOrganizer &&
          booking.isResourcelessEvent == true
      );
    } else if (type == 'with-resource') {
      return bookings.filter(
        (booking: CalendarEvent) =>
          booking.isOrganizer == isOrganizer &&
          booking.isResourcelessEvent == false
      );
    }
  }

  private sortBookingsByDate(bookings: CalendarEvent[]) {
    // Extract all booking IDs to keep track of existing bookings
    const bookingsSortedByDate: BookingsSortedByDate = {};
    let bookingsIds = bookings.map((booking) => booking.id);
    // bookings = this.floorplanSearchService.mapBookingsListToItsFloorMap(bookings);
    bookings.forEach((booking) => {
      // Convert booking start time to a Date object and format it to YYYY-MM-DD
      const startDate = new Date(booking.startTime);
      const bookingDate = moment(startDate).format('YYYY-MM-DD');
      // If the date is not already a key in bookingsSortedByDate, initialize it with an empty array
      if (!bookingsSortedByDate[bookingDate]) {
        bookingsSortedByDate[bookingDate] = [];
      }

      // Check if the booking already exists in the sorted bookings for the current date
      const existingBookingIndex = bookingsSortedByDate[bookingDate].findIndex(
        (existingBooking) => existingBooking.id === booking.id
      );
      // If the booking does not exist, add it to the sorted bookings for the current date
      if (existingBookingIndex == -1) {
        bookingsSortedByDate[bookingDate].push(booking);
        // Sort the bookings based on startTime inside the array of the found date
        bookingsSortedByDate[bookingDate].sort(
          (a, b) =>
            new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );
      } else {
        // If the booking exists, update it in the sorted bookings for the current date
        bookingsSortedByDate[bookingDate][existingBookingIndex] = booking;
      }
    });

    // Remove bookings that are no longer in the bookings array
    for (const date in bookingsSortedByDate) {
      bookingsSortedByDate[date].forEach((booking) => {
        // If the booking ID is not in the bookingsIds array, remove it from the sorted bookings
        if (!bookingsIds.includes(booking.id)) {
          bookingsSortedByDate[date].splice(
            bookingsSortedByDate[date].indexOf(booking),
            1
          );
        }
      });
      // If all bookings for a date have been removed, delete the date from the bookingsSortedByDate object
      if (bookingsSortedByDate[date].length === 0) {
        delete bookingsSortedByDate[date];
      }
    }
    return bookingsSortedByDate;
  }
}
